import { supabase } from '../supabaseClient';

/**
 * 기본 Google 공개 STUN 서버 목록 (Edge Function 장애 시 무중단 폴백용)
 */
export const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * 모듈 레벨 인메모리 캐시 변수
 */
let cachedIceServers = null;
let cachedExpiresAt = 0; // 만료 시각 (Epoch ms)
let cachedFetchedAt = 0; // 발급 수신 시각 (Epoch ms)
let pendingFetchPromise = null; // 동시 다중 호출 시 중복 요청 방지용 Promise

// 만료 5분(300,000ms) 전부터 만료로 간주하여 선제 갱신
const EXPIRATION_BUFFER_MS = 5 * 60 * 1000;

/**
 * Cloudflare TURN 서버 목록에서 핵심 서버(STUN 1개, TURN 3개)만 추출하는 순수 함수
 * - STUN: Cloudflare stun 3478 1개 (Google STUN은 정상 경로에서 제외)
 * - TURN: ① turn 3478 udp  ② turn 80 tcp  ③ turns 443 tcp (총 3개)
 * - 제외: turn 53 udp, turn 3478 tcp, turns 5349 tcp 등 나머지
 * - 안전장치: 선별 결과 TURN이 0개이면 원본 목록을 그대로 보존
 * 
 * @param {Array<RTCIceServer>} rawServers 
 * @returns {Array<RTCIceServer>}
 */
export function trimIceServers(rawServers) {
  if (!Array.isArray(rawServers) || rawServers.length === 0) {
    return rawServers;
  }

  const trimmed = [];
  let stunAdded = false;
  let turnUdp3478Added = false;
  let turnTcp80Added = false;
  let turnsTcp443Added = false;

  for (const server of rawServers) {
    if (!server || !server.urls) continue;
    const urlList = Array.isArray(server.urls) ? server.urls : [server.urls];
    const keptUrls = [];

    for (const u of urlList) {
      if (typeof u !== 'string') continue;

      // STUN : Cloudflare stun 3478 1개만
      if (/^stun:[^?]+:3478(?:\?|$)/i.test(u)) {
        if (!stunAdded) {
          keptUrls.push(u);
          stunAdded = true;
        }
        continue;
      }

      // TURN ① turn 3478 udp (transport 지정이 없거나 udp인 경우)
      if (/^turn:[^?]+:3478(?:\?|$)/i.test(u) && !/transport=tcp/i.test(u)) {
        if (!turnUdp3478Added) {
          keptUrls.push(u);
          turnUdp3478Added = true;
        }
        continue;
      }

      // TURN ② turn 80 tcp
      if (/^turn:[^?]+:80(?:\?|$)/i.test(u) && /transport=tcp/i.test(u)) {
        if (!turnTcp80Added) {
          keptUrls.push(u);
          turnTcp80Added = true;
        }
        continue;
      }

      // TURN ③ turns 443 tcp
      if (/^turns:[^?]+:443(?:\?|$)/i.test(u) && /transport=tcp/i.test(u)) {
        if (!turnsTcp443Added) {
          keptUrls.push(u);
          turnsTcp443Added = true;
        }
        continue;
      }
    }

    if (keptUrls.length > 0) {
      trimmed.push({
        ...server,
        urls: Array.isArray(server.urls) ? keptUrls : keptUrls[0],
      });
    }
  }

  // 안전장치: 골라 낸 결과에 TURN이 1개도 없으면 받은 원본 목록을 그대로 쓴다
  const hasTurn = trimmed.some((s) => {
    const list = Array.isArray(s.urls) ? s.urls : [s.urls];
    return list.some((u) => typeof u === 'string' && /^turns?:/i.test(u));
  });

  if (!hasTurn) {
    if (import.meta.env.DEV) {
      console.warn('[iceServers] trimIceServers: TURN 서버가 0개로 검출되어 원본 목록을 보존합니다.');
    }
    return rawServers;
  }

  return trimmed;
}

/**
 * Cloudflare TURN 임시 자격증명 및 STUN 서버 목록 획득 함수
 * - 유효한 캐시가 있으면 즉시 반환
 * - 캐시가 없거나 만료 임박 시 get-turn-credentials Edge Function 호출
 * - 호출 실패 시 기본 Google STUN 목록으로 안전 폴백 (조용한 실패 방지)
 * 
 * @returns {Promise<Array<RTCIceServer>>} WebRTC RTCIceServer 배열
 */
export async function getIceServers() {
  const now = Date.now();

  // 1. 유효한 캐시 확인 (만료 5분 전까지 유효)
  if (cachedIceServers && cachedExpiresAt - now > EXPIRATION_BUFFER_MS) {
    return cachedIceServers;
  }

  // 2. 이미 진행 중인 조회가 있다면 재사용 (중복 RTT 방지)
  if (pendingFetchPromise) {
    return pendingFetchPromise;
  }

  // 3. 신규 자격증명 발급 비동기 작업
  pendingFetchPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-turn-credentials', {
        body: {},
      });

      if (error || !data || !data.iceServers || !Array.isArray(data.iceServers)) {
        console.error('[iceServers] TURN 자격증명 발급 실패, 기본 Google STUN으로 폴백합니다:', error || data);
        return DEFAULT_ICE_SERVERS;
      }

      // [수정 (가)] Cloudflare 목록 중 STUN 1개, TURN 3개만 선별 (Google STUN 2개는 정상 경로에서 제외)
      const trimmedIceServers = trimIceServers(data.iceServers);

      if (import.meta.env.DEV) {
        const urlSummaries = [];
        trimmedIceServers.forEach((s) => {
          const list = Array.isArray(s.urls) ? s.urls : [s.urls];
          list.forEach((u) => {
            if (typeof u === 'string') urlSummaries.push(u);
          });
        });
        console.log('[ICE 서버 주소 목록]', urlSummaries.join(' / '));
      }

      // 만료 시각 계산 및 캐싱
      let expiresAtMs = 0;
      if (data.expiresAt) {
        expiresAtMs = new Date(data.expiresAt).getTime();
      }
      if (!expiresAtMs || isNaN(expiresAtMs)) {
        expiresAtMs = now + ((data.expiresIn || 86400) * 1000);
      }

      cachedIceServers = trimmedIceServers;
      cachedExpiresAt = expiresAtMs;
      cachedFetchedAt = now;

      return trimmedIceServers;
    } catch (err) {
      console.error('[iceServers] TURN 자격증명 발급 예외 발생, 기본 Google STUN으로 폴백합니다:', err);
      return DEFAULT_ICE_SERVERS;
    } finally {
      pendingFetchPromise = null;
    }
  })();

  return pendingFetchPromise;
}

/**
 * [결함 ④ 작업 2-A] ICE restart 재연결용 출입증(TURN 자격증명) 확인 및 갱신 함수
 * - 저장본을 받은 지 12시간이 지났거나, 남은 유효 시간을 알 수 없거나, 만료 5분 전이면 새로 발급
 * - 유효한 경우 기존 캐시 재사용
 * 
 * @returns {Promise<{ iceServers: Array<RTCIceServer>, refreshed: boolean }>}
 */
export async function getIceServersForRestart() {
  const now = Date.now();
  const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

  const isExpiredOrUnknown = !cachedExpiresAt || (cachedExpiresAt - now <= EXPIRATION_BUFFER_MS);
  const isOlderThan12Hours = !cachedFetchedAt || (now - cachedFetchedAt >= TWELVE_HOURS_MS);

  if (!cachedIceServers || isExpiredOrUnknown || isOlderThan12Hours) {
    clearIceServersCache();
    const newServers = await getIceServers();
    return { iceServers: newServers, refreshed: true };
  }

  return { iceServers: cachedIceServers, refreshed: false };
}

/**
 * 로그아웃 또는 세션 초기화 시 메모리 캐시 정리 함수
 */
export function clearIceServersCache() {
  cachedIceServers = null;
  cachedExpiresAt = 0;
  cachedFetchedAt = 0;
  pendingFetchPromise = null;
}

/**
 * [F구역] 유효한 진짜 TURN 자격증명 보관분이 존재하는지 동기 확인하는 함수
 * - 보관분이 존재하고
 * - 기본 STUN 대체값이 아니며
 * - 유효시간이 5분 이상 남아 있고
 * - 실제 TURN 서버(turn: / turns:)를 1개 이상 포함할 때만 true 반환
 * 
 * @returns {boolean}
 */
export function hasValidTurnCache() {
  if (!cachedIceServers || !Array.isArray(cachedIceServers) || cachedIceServers.length === 0) {
    return false;
  }
  if (cachedIceServers === DEFAULT_ICE_SERVERS) {
    return false;
  }
  const now = Date.now();
  if (cachedExpiresAt - now <= EXPIRATION_BUFFER_MS) {
    return false;
  }
  return cachedIceServers.some((s) => {
    const list = Array.isArray(s?.urls) ? s.urls : [s?.urls];
    return list.some((u) => typeof u === 'string' && /^turns?:/i.test(u));
  });
}

